/**
 * Placeholder-launch site configuration — the single file to edit when standing
 * up the static ridereserv.com placeholder for Twilio toll-free verification.
 *
 * Why this exists: Twilio rejected the toll-free verification (codes 30489
 * "website must be established and active" and 30513 "opt-in language unclear").
 * Satisfying them needs a live, credible site with a real business identity and
 * an explicit, public SMS opt-in/consent page. The values below are consumed by
 * the real legal pages (`/privacy`, `/terms`, `/driver-terms`, `/sms`) and the
 * site footer, so they propagate to the live site too — this is NOT throwaway
 * copy. Only the deploy-time bits (`_redirects.placeholder`, the build script)
 * are placeholder-specific.
 *
 * BEFORE DEPLOYING: fill in every field marked `FILL IN`. The business name,
 * mailing address, and contact details MUST match what is entered in the Twilio
 * toll-free verification submission (entity: Arcnaut, LLC), or it is rejected
 * again. An empty address/email is omitted gracefully from the rendered pages,
 * so a missing value fails silently — double-check before resubmitting.
 */

/** A US-style postal address, split into parts for flexible rendering. */
interface PostalAddress {
  /** Street line (e.g. "123 Main St, Suite 200"). */
  street: string;
  /** City. */
  city: string;
  /** State / province (e.g. "CA"). */
  state: string;
  /** Postal / ZIP code. */
  zip: string;
}

/** Canonical RideReserv / Arcnaut, LLC business identity shown across the site. */
interface SiteIdentity {
  /** Registered legal entity — must match the Twilio submission. */
  legalEntity: string;
  /** Doing-business-as / brand name. */
  dba: string;
  /** Public site origin (no trailing slash). */
  websiteUrl: string;
  /** Mailing address (leave blank parts to omit the address line). */
  address: PostalAddress;
  /** General support / contact email (the one shown publicly). */
  supportEmail: string;
  /** Privacy-requests email. Leave blank to reuse {@link SiteIdentity.supportEmail}. */
  privacyEmail: string;
  /** Human-readable support phone (e.g. "(866) 650-8539"). */
  supportPhoneDisplay: string;
  /** E.164 support phone for the `tel:` href (e.g. "+18666508539"). */
  supportPhoneE164: string;
  /** US state whose law governs the Terms (e.g. "Delaware"). */
  governingLawState: string;
}

export const SITE: SiteIdentity = {
  legalEntity: "Arcnaut, LLC", // confirmed from the Twilio rejection notice
  dba: "RideReserv",
  websiteUrl: "https://ridereserv.com",

  // FILL IN — required for Twilio code 30489 (must match the submission).
  address: {
    street: "",
    city: "",
    state: "",
    zip: "",
  },

  // Public contact email. Use a domain alias that forwards to a monitored inbox
  // (e.g. Cloudflare Email Routing: support@ridereserv.com → your personal inbox)
  // — never a personal address, which would then be public. ONE alias is enough:
  // privacyEmail falls back to this when left blank.
  supportEmail: "support@ridereserv.com",
  privacyEmail: "privacy@ridereserv.com",

  // Support phone — OPTIONAL. Twilio doesn't require a phone (email/website
  // contact is enough), so this is blank: no phone is shown anywhere on the site
  // and contact falls back to email. To display a support/HELP line, fill both
  // fields (e.g. the verified toll-free "(866) 650-8539" / "+18666508539").
  supportPhoneDisplay: "",
  supportPhoneE164: "",

  // FILL IN — the US state in the Terms "Governing Law" section.
  governingLawState: "New York",
};

/**
 * Whether a usable mailing address has been filled in. Components render the
 * address line only when this is true, so a blank address omits cleanly rather
 * than printing an empty "· ," fragment.
 */
export const HAS_ADDRESS: boolean =
  SITE.address.street.trim().length > 0 && SITE.address.city.trim().length > 0;

/**
 * Whether a support phone has been configured. Components render the phone link
 * only when this is true, so a blank phone omits cleanly and contact falls back
 * to email. Twilio toll-free verification does not require a phone.
 */
export const HAS_PHONE: boolean =
  SITE.supportPhoneDisplay.trim().length > 0 && SITE.supportPhoneE164.trim().length > 0;

/**
 * The email shown for privacy requests, falling back to the support email when
 * {@link SITE.privacyEmail} is blank — so a single configured alias covers both.
 */
export const PRIVACY_EMAIL: string = SITE.privacyEmail.trim() || SITE.supportEmail;

/**
 * The mailing address rendered as a single inline string, or an empty string
 * when {@link HAS_ADDRESS} is false.
 * @returns The formatted one-line address, or "" if not configured.
 */
export function formatAddressInline(): string {
  if (!HAS_ADDRESS) return "";
  const { street, city, state, zip } = SITE.address;
  return `${street}, ${city}, ${state} ${zip}`.trim();
}

// ---------------------------------------------------------------------------
// SMS program copy — fixes Twilio code 30513 (opt-in language unclear) and the
// related SMS-terms requirements (30522). Kept here so the `/sms` page and the
// Twilio `message_flow` field use the exact same wording.
// ---------------------------------------------------------------------------

/** The SMS program / campaign name. */
export const SMS_PROGRAM_NAME = "RideReserv Booking & Account Alerts";

/** One-sentence description of what the SMS program is. */
export const SMS_PROGRAM_DESCRIPTION =
  "RideReserv sends text messages about your bookings and account: booking " +
  "confirmations, ride and dispatch status updates, driver assignment notices, " +
  "reminders, and one-time login/verification codes.";

/** The message categories users receive, for the bulleted disclosure. */
export const SMS_MESSAGE_TYPES: readonly string[] = [
  "Booking confirmations",
  "Ride and dispatch status updates",
  "Driver assignment notices",
  "Reminders",
  "One-time login and verification codes",
];

/**
 * The exact opt-in consent statement a user agrees to. Explicitly names SMS,
 * states the message types, declares frequency + rates, the STOP/HELP keywords,
 * and that consent is optional — everything Twilio 30513 requires, and worded so
 * the `/sms` page and the submitted `message_flow` match verbatim.
 */
export const SMS_CONSENT_TEXT =
  "I agree to receive recurring SMS text messages from RideReserv about my " +
  "booking and account (confirmations, ride and dispatch updates, reminders, " +
  "and login codes). Message frequency varies. Message and data rates may " +
  "apply. Reply STOP to opt out, HELP for help. Consent is not a condition of " +
  "any purchase or of using RideReserv.";
